import React, { useMemo, useState } from "react";

import { ShareDialog } from "#app/components/sharing/share-dialog";
import { useWorkspace } from "#app/lib/data/app-provider";
import type { EntityData } from "#app/lib/entity-visuals";
import { useIssueShareHeader } from "#app/lib/hooks/use-issue-share-header";
import { useItemSharing } from "#app/lib/hooks/use-item-sharing";
import { issueHref } from "#app/lib/utils/entity-hrefs";

// Item-specific wrapper around the generic ShareDialog. Reuses the
// useItemSharing hook for share data + mutations and useIssueShareHeader
// for the issue's display name, icon and workflow — the dialog itself
// stays a thin component.

type ItemShareDialogProps = {
  issueId: string | null;
  onOpenChange: (open: boolean) => void;
};

export function ItemShareDialog({
  issueId,
  onOpenChange,
}: ItemShareDialogProps) {
  const { activeWorkspace } = useWorkspace();
  const [activeSearch, setActiveSearch] = useState<string | undefined>(
    undefined,
  );

  const header = useIssueShareHeader(issueId);
  const sharing = useItemSharing(issueId, activeSearch);

  // Issue type "name" maps to "Project"/"Account"/"Contact" — the copy uses
  // the lower-cased form so it reads naturally.
  const issueTypeName = header.issueTypeName ?? "Project";
  const subject = useMemo(
    (): EntityData | null =>
      issueId && header.name != null
        ? {
            kind: "issue",
            id: issueId,
            issue: { name: header.name, icon: header.icon },
          }
        : null,
    [issueId, header.name, header.icon],
  );
  // The issue's deep link: its workflow board with the issue overlay open.
  const shareUrl =
    issueId && header.workflowId && activeWorkspace
      ? issueHref(activeWorkspace.urlPath, header.workflowId, issueId)
      : null;

  return (
    <ShareDialog
      open={issueId != null}
      onOpenChange={onOpenChange}
      subject={subject}
      subjectNoun={issueTypeName.toLowerCase()}
      shareUrl={shareUrl}
      sharing={sharing}
      onSearchChange={setActiveSearch}
    />
  );
}
